import Base from '@studiometa/js-toolkit';
import Accordion from '@studiometa/js-toolkit/components/Accordion';
import nextFrame from '@studiometa/js-toolkit/utils/nextFrame';
import Cycle from './Cycle';
import Tabs from './Tabs';
import ShowMore from './ShowMore';
import Table from './Table';
import ScrollTo from './ScrollToAnchor';
import Slider from './Slider';
import Cache from '../utils/Cache';
import { push as historyPushState } from '../utils/history';
import { isDev } from '../config';

const cache = new Cache({ name: 'tabs' });

/**
 * TabNav component
 * @see /templates/tabs/components/container.twig
 *
 * TabNav component is using native browser History API
 * @see https://developer.mozilla.org/en-US/docs/Web/API/History
 */
export default class TabNav extends Base {
  /**
   * Component Configuration
   */
  get config() {
    return {
      name: 'TabNav',
      log: isDev(),
      components: {
        Accordion,
        Tabs,
        '.wysiwyg table': Table,
        'a[href*="#"]': ScrollTo,
        Slider,
        ShowMore,
        Cycle,
      },
    };
  }

  /**
   * Get tabs refs by url
   */
  get tabsByUrl() {
    return this.$refs.tab.reduce((acc, tab) => {
      acc[tab.dataset.url] = tab;
      return acc;
    }, {});
  }

  /**
   * Get next tab element
   * @return {HTMLElement}
   */
  get nextTab() {
    return this.$refs.tab[this.currentTabIndex + 1] || this.$refs.tab[0];
  }

  /**
   * Get the current active tab element.
   * @return {HTMLElement}
   */
  get currentTab() {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');

    if (!tabParam) {
      return this.$refs.tab[0];
    }

    const currentTab = this.$refs.tab.find((tab) => tab.dataset.url === tabParam);

    return currentTab || this.$refs.tab[0];
  }

  /**
   * Get the current active tab index.
   * @return {Number}
   */
  get currentTabIndex() {
    return this.$refs.tab.indexOf(this.currentTab);
  }

  /**
   * @deprecated Use `currentTabIndex` instead.
   */
  get current() {
    return this.currentTabIndex;
  }

  /**
   * Mounted hook
   */
  mounted() {
    window.addEventListener('popstate', this.onPopstate);
    this.updateNextContent();

    // Prefetch all tab content when the page is ready.
    window.requestIdleCallback(() => {
      this.$refs.tab.forEach((tab) => {
        this.fetch(tab, { pushState: false, updateContent: false });
      });
    });
  }

  /**
   * Destroyed hook
   */
  destroyed() {
    window.removeEventListener('popstate', this.onPopstate);
  }

  /**
   * Go to tab
   * @param {String} url  Tab slug
   * @param {String} hash The hash of a potential element in the new content to scroll to.
   */
  goTo(url, hash = '') {
    this.fetch(this.tabsByUrl[url], { hash });
  }

  /**
   * Go to next tab
   */
  next() {
    const urls = Object.keys(this.tabsByUrl);
    const nextUrl = urls[this.currentTabIndex + 1];

    this.goTo(nextUrl || urls[0]);
  }

  /**
   * Get Tab Content
   *
   * @param {Event} event The original event instance.
   */
  onTabClick(event) {
    event.preventDefault();
    const { target } = event;
    return this.fetch(target);
  }

  /**
   * Handler for the click event on the next button.
   *
   * @param {MouseEvent} event The event's object.
   */
  onNextClick(event) {
    event.preventDefault();
    this.next();
  }

  /**
   * Fetch tab content.
   *
   * @param {HTMLElement} target
   * @param {Boolean}     options.pushState     Wether to update the URL with the new state or not.
   * @param {Boolean}     options.updateContent Wether to update the content of the page or not (used to prefetch).
   * @param {String}      options.hash          The hash to look for when scrolling to content.
   */
  async fetch(target, { pushState = true, updateContent = true, hash = '' } = {}) {
    const targetTab = target || this.$refs.tab[0];
    const { endpoint, url } = targetTab.dataset;
    let data = cache.get(url);

    // Return Cache if existant
    if (!data) {
      // Fetch Tab Content data
      data = await fetch(endpoint)
        .catch((error) => {
          throw new Error(
            `Error while fetching data from ${endpoint} in TabNav.js : ${error.message}`
          );
        })
        .then((response) => response.text());

      // Save data in cache
      cache.set(url, data);
    }

    if (pushState) {
      historyPushState({ search: { tab: url }, hash }, { url });
    }

    // Update the TabContent
    if (updateContent) {
      this.updateTabContent(data, hash);
    }
  }

  /**
   * Update Tab content.
   *
   * @param {String} data The HTML to inject in the template
   * @param {String} hash An ID to scroll to when the new content has been injected.
   */
  updateTabContent(data, hash) {
    this.updateNextContent();

    this.$refs.tab.forEach((tab) => {
      tab.classList.remove('is-active');
    });
    this.currentTab.classList.add('is-active');

    // Replace the Tab content
    this.$refs.contentTab.innerHTML = data;

    nextFrame(() => {
      this.$update();
      const target = hash ? this.$el.querySelector(hash) || this.$el : this.$el;
      target.scrollIntoView({
        behavior: 'smooth',
      });
    });

    this.$emit('tabChange');
  }

  /**
   * Update the content of the `next` ref.
   */
  updateNextContent() {
    if (!this.$refs.next) {
      return;
    }

    this.$refs.next.setAttribute('href', this.nextTab.href);
    if (this.$refs.nextLabel) {
      this.$refs.nextLabel.innerHTML = `<span class="z-medium">${this.nextTab.innerHTML}</span>`;
    } else {
      this.$refs.next.innerHTML = `<span class="z-medium">${this.nextTab.innerHTML}</span>`;
    }
  }

  /**
   * History JS API popState event
   * Replace the TabContent with previous state
   *
   * @param {Event} event The original event instance.
   */
  onPopstate(event) {
    if (event.state && event.state.url) {
      const tab = this.tabsByUrl[event.state.url];
      if (tab) {
        this.fetch(tab, { pushState: false });
      }
    }
  }
}
